import UserManager from "src/userManager";
import { useFeUserStore } from "src/store/FeUserStore";

const externalApiBaseUrl =
  import.meta.env.VITE_MOSAIC_EXTERNAL_API_BASE_URL || "https://localhost:5003";

// Helper to get impersonated user ID from zustand store
const getImpersonatedUserId = (): string | null => {
  const userId = useFeUserStore.getState().impersonateUser?.userId;
  if (userId && userId !== 0) {
    return userId.toString();
  }
  return null;
};

const prependUrl = (url: string) => {
  // if url starts with http or https, return it as is
  if (url.startsWith("http://") || url.startsWith("https://")) {
    return url;
  }

  // if url starts with a slash, remove it
  if (url.startsWith("/")) {
    url = url.substring(1);
  }

  // Ensure base URL ends with slash for correct URL resolution
  const base = externalApiBaseUrl.endsWith("/") ? externalApiBaseUrl : externalApiBaseUrl + "/";
  return new URL(url, base).toString();
};
class Api {
  async fetchNoHeaders(url: string) {
    return await fetch(prependUrl(url));
  }

  async fetchSimple(url: string) {
    return await fetch(prependUrl(url), await this.DefaultHeaders);
  }

  async fetch(url: string, requestInit: RequestInit): Promise<Response> {
    // Auto-inject impersonation header if not already present
    const impersonatedUserId = getImpersonatedUserId();
    if (impersonatedUserId) {
      const headers = new Headers(requestInit.headers || undefined);
      if (!headers.has("X-Impersonate-User-Id")) {
        headers.set("X-Impersonate-User-Id", impersonatedUserId);
      }
      requestInit.headers = headers;
    }
    const response = await fetch(prependUrl(url), requestInit);
    return response;
  }

  // create a post
  async post(url: string, data: any) {
    let requestInit = await this.DefaultHeaders;

    requestInit.method = "POST";
    requestInit.body = JSON.stringify(data);
    requestInit.headers = {
      ...requestInit.headers,
      "Content-Type": "application/json",
    };

    return await this.fetch(url, requestInit);
  }

  async fetchWithRetry(url: string, requestInit: RequestInit) {
    return await this.fetchWithRetryLimit(url, requestInit, 100);
  }

  async fetchWithRetryLimit(url: string, requestInit: RequestInit, retryCount: number = 3) {
    let response: any;

    for (var i = 0; i < retryCount; i++) {
      response = await this.fetch(url, requestInit);
      if (response.status === 202) {
        await sleep(3000);
      } else {
        return response;
      }
    }

    return response;
  }

  get DefaultHeaders(): Promise<RequestInit> {
    return (async () => {
      const user = await UserManager.getUser();
      if (!user || !user.access_token) {
        throw new Error("User is not authenticated or access token is missing.");
      }
      const headers: Record<string, string> = { 
        Authorization: `Bearer ${user.access_token}` 
      };
      
      // Add impersonation header if impersonating
      const impersonatedUserId = getImpersonatedUserId();
      if (impersonatedUserId) {
        headers['X-Impersonate-User-Id'] = impersonatedUserId;
      }
      
      return {
        headers,
        credentials: 'include',
      } as RequestInit;
    })();
  }

  get DefaultHeadersNoCache(): Promise<RequestInit> {
    return (async () => {
      const user = await UserManager.getUser();
      if (!user || !user.access_token) {
        throw new Error("User is not authenticated or access token is missing.");
      }
      const headers: Record<string, string> = {
        Authorization: `Bearer ${user.access_token}`,
        pragma: "no-cache",
        "cache-control": "no-cache",
      };
      
      // Add impersonation header if impersonating
      const impersonatedUserId = getImpersonatedUserId();
      if (impersonatedUserId) {
        headers['X-Impersonate-User-Id'] = impersonatedUserId;
      }
      
      return {
        headers,
        credentials: 'include',
      };
    })();
  }

  get JsonHeaders() {
    return (async () => {
      const user = await UserManager.getUser();
      if (!user || !user.access_token) {
        throw new Error("User is not authenticated or access token is missing.");
      }
      const headers: Record<string, string> = {
        Authorization: `Bearer ${user.access_token}`,
        "Content-type": "application/json",
      };
      
      // Add impersonation header if impersonating
      const impersonatedUserId = getImpersonatedUserId();
      if (impersonatedUserId) {
        headers['X-Impersonate-User-Id'] = impersonatedUserId;
      }
      
      return {
        headers,
        credentials: 'include',
      };
    })();
  }
}

async function sleep(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export default new Api();
